<div class="iv"
     [class.iv--expanded]="expanded">

  @let isLoading = loadingList || loadingDetail;
  @let hasVerifications = verifications.length > 0;

  <!-- Loading skeleton -->
  @if (isLoading) {
  <div class="iv-row iv-row--skeleton">
    <div class="s26-skeleton s26-skeleton--btn"></div>
    <div class="s26-skeleton s26-skeleton--id"></div>
    <div class="s26-skeleton s26-skeleton--chip"></div>
    <div class="s26-skeleton s26-skeleton--dots"></div>
    <div class="s26-skeleton s26-skeleton--date"></div>
    <span class="s26-spacer"></span>
    <div class="s26-skeleton s26-skeleton--dots"></div>
  </div>
  }

  <!-- Empty state -->
  @if (!isLoading && !hasVerifications) {
  <div class="iv-row iv-row--empty">
    <mat-icon class="iv-empty__icon">verified_user</mat-icon>
    <span class="s26-kv-val">{{
      'verification.id.noVerificationMessage' | translate
      }}</span>
    <button mat-stroked-button
            color="primary"
            class="iv-start-btn"
            (click)="startVerification()">
      {{ 'verification.id.startVerificationMessage' | translate }}
    </button>
  </div>
  }

  <!-- Main content -->
  @if (!isLoading && hasVerifications) {

  @let activeCount = activeVerificationsCount;
  @let isCurrentActive = currentVerificationIsActive;
  @let selectedFlowOption = detailView ? flowOptionFor(detailView.flow) : null;

  <!-- Collapsed row (always visible) -->
  <div class="iv-row"
       [class.iv-row--loading]="loadingDetail"
       (click)="toggleExpanded()">

    <!-- Date navigation dropdown -->
    <div class="iv-nav">
      <button class="iv-nav__trigger"
              [matMenuTriggerFor]="verificationMenu"
              [disabled]="!hasVerifications"
              (click)="$event.stopPropagation()"
              (menuOpened)="newVerificationIds.clear()">
        @if (newVerificationIds.size > 0) {
        <div class="iv-nav__dot"></div>
        } @else {
        <mat-icon>arrow_drop_down</mat-icon>
        }
        <span class="s26-kv-val">{{
          detailView?.startedAt | date: 'dd.MM.yy HH:mm'
          }}</span>
      </button>

      <mat-menu #verificationMenu="matMenu"
                class="iv-nav-menu">
        @for (item of verificationItems; track item.id; let i = $index) {
        <button mat-menu-item
                class="iv-nav-menu__item"
                [class.iv-nav-menu__item--active]="i === currentIndex"
                (click)="selectVerification(i)">
          <span class="iv-nav-menu__row">
            <span class="s26-kv-val">{{
              item.startedAt | date: 'dd.MM.yy HH:mm'
              }}</span>
            <span class="s26-chip s26-chip--{{ item.stateClass }} s26-chip--xs">
              {{ 'verification.id.state.' + item.state | translate }}
            </span>
          </span>
        </button>
        }
      </mat-menu>
    </div>

    <div class="s26-sep"></div>

    <!-- State chip -->
    @if (detailView) {
    <div class="s26-chip s26-chip--{{ detailView.stateClass }}">
      {{ 'verification.id.state.' + detailView.state | translate }}
    </div>
    } @else {
    <span class="iv-id iv-id--ghost">—</span>
    }

    <div class="s26-sep"></div>

    <!-- Flow logo and label -->
    @if (selectedFlowOption) {
    <span class="iv-flow">
      <img class="iv-flow__logo"
           [src]="selectedFlowOption.iconUrl"
           [alt]="selectedFlowOption.label" />
      <span class="s26-kv-val">{{ selectedFlowOption.label }}</span>
    </span>
    }

    <span class="s26-spacer"></span>

    <!-- Action menu -->
    <itfg-mat-tab-menu class="iv__menu-btn"
                       yPosition="above"
                       (click)="toggleExpanded()">
      <div trigger><mat-icon>more_horiz</mat-icon></div>
      <div content>
        <button mat-menu-item
                [disabled]="activeCount > 0"
                (click)="startVerification()">
          {{ 'verification.id.startVerificationMessage' | translate }}
        </button>
        <button mat-menu-item
                [disabled]="!isCurrentActive"
                (click)="submitDecision(verficationDecisions.VALIDATE)">
          {{ 'verification.id.validate' | translate }}
        </button>
        <button mat-menu-item
                [disabled]="!isCurrentActive"
                (click)="submitDecision(verficationDecisions.INVALIDATE)">
          {{ 'verification.id.invalidate' | translate }}
        </button>
      </div>
    </itfg-mat-tab-menu>
  </div>

  <!-- Expandable details panel -->
  <div class="iv-panel"
       [class.iv-panel--open]="expanded">
    <div class="iv-panel__inner">

      @if (loadingDetail) {
      <div class="iv-panel__loading">
        <mat-spinner diameter="22" />
      </div>
      }

      @if (!loadingDetail && detailView) {
      @let selectedEvent = selectedEventId
      ? eventById(detailView.events, selectedEventId)
      : null;

      <div class="iv-split__body">
        <!-- Left: event list -->
        <div class="iv-ev-list">
          @for (ev of detailView.events; track ev.id) {
          <div class="iv-ev-item"
               [class.iv-ev-item--active]="selectedEventId === ev.id"
               [class.iv-ev-item--clickable]="ev.hasData"
               (click)="ev.hasData && toggleEvent(ev.id)">
            <div class="iv-ev-item__dot"
                 [style.background]="ev.dotColor"></div>

            <span class="iv-ev-item__type"
                  [class.iv-ev-item__type--fail]="ev.isFail"
                  [class.iv-ev-item__type--ok]="ev.isSuccess">
              {{ ev.typeDisplay }}
            </span>

            @if (ev.documentTypeLabel) {
            <span class="iv-ev-item__doc-chip iv-ev-item__doc-chip--{{ ev.documentTypeClass }}">
              {{ ev.documentTypeLabel }}
            </span>
            }

            <span class="s26-mono iv-ev-item__ts">
              {{ ev.createdAt | date: 'HH:mm:ss' }}
            </span>
          </div>
          }
        </div>

        <div class="iv-split__divider"></div>

        <!-- Right: event data OR verification details -->
        <div class="iv-detail-pane">
          @if (selectedEvent) {
          <div class="iv-detail-pane__meta">
            <span class="s26-field">{{
              selectedEvent.createdAt | date: 'dd.MM.yyyy HH:mm:ss'
              }}</span>
            <span class="s26-proc-tag"
                  [style.background]="selectedEvent.procColorBg"
                  [style.color]="selectedEvent.procColor">
              {{ selectedEvent.processor }}
            </span>
          </div>

          @for (entry of selectedEvent.dataEntries; track entry[0]) {
          <div class="iv-event-data__row">
            <span class="iv-event-data__key">{{ entry[0] }}</span>
            <span class="iv-event-data__val s26-mono">{{ entry[1] }}</span>
          </div>
          }
          @if (!selectedEvent.hasData) {
          <span class="iv-event-data__empty">No data</span>
          }
          } @else {
          <div class="s26-field">
            <span>id</span>
            <span class="s26-mono">#{{ detailView.id }}</span>
          </div>
          <div class="s26-field">
            <span>{{ 'verification.id.flow.label' | translate }}</span>
            <span class="s26-mono">{{
              'verification.id.flow.' + detailView.flow | translate
              }}</span>
          </div>
          <div class="s26-field">
            <span>{{ 'verification.id.startedAt' | translate }}</span>
            <span>{{ detailView.startedAt | date: 'dd.MM.yy HH:mm' }}</span>
          </div>
          <div class="s26-field">
            <span>{{ 'verification.id.endedAt' | translate }}</span>
            <span>{{
              (detailView.endedAt | date: 'dd.MM.yy HH:mm') ?? '—'
              }}</span>
          </div>
          <div class="s26-field">
            <span>{{ 'verification.id.duration' | translate }}</span>
            <span>{{ detailView.duration }}</span>
          </div>

          @if (detailView.miReportId) {
          <div class="s26-field">
            <span>{{ 'verification.id.miReport' | translate }}</span>
            <span class="s26-mono">#{{ detailView.miReportId }}</span>
          </div>
          }
          }
        </div>
      </div>
      }
    </div>
  </div>
  }
</div>
